import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Link, type LinkProps } from '@mui/material'
import { forwardRef } from 'react'

import { MUTED_TEXT, NAVY, visuallyHidden } from './styles'

// A scheme is letters then a colon not followed by a digit, so the port in
// "example.org:8080" doesn't read as one.
const SCHEME = /^[a-z][a-z0-9+.-]*:(?!\d)/i

/**
 * An http or https URL for a link typed into the database, or null when it
 * can't be made safe. Adds https:// when the scheme is missing.
 */
export function safeHref(raw: string | null | undefined): string | null {
  const text = raw?.trim()
  if (!text) return null
  const withScheme = SCHEME.test(text) ? text : `https://${text.replace(/^\/+/, '')}`
  try {
    const url = new URL(withScheme)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

/**
 * "View website", named for its opportunity so a list of them can be told
 * apart, and saying it opens a new tab. Forwards its ref and props so a
 * Tooltip can attach its description to the link itself. A link that isn't
 * http or https shows as "None provided", the same as no link at all.
 */
const WebsiteLink = forwardRef<
  HTMLAnchorElement,
  LinkProps & { href: string; opportunity: string }
>(function WebsiteLink({ href, opportunity, sx, ...rest }, ref) {
  const safe = safeHref(href)
  if (!safe) {
    return (
      <Box component="span" sx={{ fontSize: '14px', color: MUTED_TEXT }}>
        None provided
      </Box>
    )
  }
  return (
    <Link
      {...rest}
      ref={ref}
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: NAVY, ...sx }}
    >
      View website
      <Box component="span" sx={visuallyHidden}>
        {` for ${opportunity} (opens in new tab)`}
      </Box>
      <OpenInNewIcon
        aria-hidden="true"
        sx={{ fontSize: 14, ml: 0.375, verticalAlign: 'text-bottom' }}
      />
    </Link>
  )
})

export default WebsiteLink
